import { useEffect, useState } from "react";

const GOOGLE_MAPS_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

let loadPromise: null | Promise<void> = null;

export function useGoogleMapsScript(): { ready: boolean } {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadScript().then(() => setReady(true));
  }, []);

  return { ready };
}

function loadScript(): Promise<void> {
  if (loadPromise) return loadPromise;

  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  // Already loaded
  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  loadPromise = (async () => {
    // Use the async bootstrap loader to avoid the "loaded without loading=async" warning.
    // This dynamically injects the script with loading=async and imports the places library.
    const { importLibrary, setOptions } =
      await import("@googlemaps/js-api-loader");
    setOptions({ key: GOOGLE_MAPS_API_KEY });
    await importLibrary("places");

    // Inject styles for the PlaceAutocompleteElement dark theme
    const style = document.createElement("style");
    style.textContent = `
      gmp-place-autocomplete {
        --gmpx-color-surface: #2a2a2a;
        --gmpx-color-on-surface: #fff;
        --gmpx-color-on-surface-variant: #a6a6a6;
        --gmpx-color-primary: #C5A855;
        width: 100%;
      }
      gmp-place-autocomplete::part(input) {
        background-color: #1e1e1e;
        color: #fff;
        border: 1px solid #444;
        border-radius: 12px;
        padding: 14px 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 16px;
        outline: none;
        box-sizing: border-box;
        width: 100%;
      }
      gmp-place-autocomplete::part(predictions) {
        background-color: #2a2a2a;
        border: 1px solid #444;
        border-radius: 12px;
        margin-top: 4px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        z-index: 999999 !important;
        position: relative;
      }
      /* Ensure the autocomplete overlay is above React Native Web Modal */
      .pac-container {
        z-index: 999999 !important;
      }
    `;
    document.head.appendChild(style);
  })();

  return loadPromise;
}
